import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, AlertCircle } from 'lucide-react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";

interface ChatMessage {
  query: string;
  answer: string;
  timestamp?: string;
}

interface SmartDocChatProps {
  documentId: string;
  onError?: (error: string) => void;
}

const SmartDocChat: React.FC<SmartDocChatProps> = ({ documentId, onError }) => {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [chatQuery, setChatQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load chat history when component mounts
    fetchChatHistory();
  }, [documentId]);

  const fetchChatHistory = async () => {
    try {
      const response = await fetch(`/api/v1/smart-documents/${documentId}/chat-history`);
      if (!response.ok) {
        throw new Error('Failed to fetch chat history');
      }
      const history = await response.json();
      setChatHistory(history);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load chat history';
      setError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const handleChat = async () => {
    if (!chatQuery.trim()) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/v1/smart-documents/${documentId}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: chatQuery }),
      });

      if (!response.ok) {
        throw new Error('Failed to process chat query');
      }

      const data = await response.json();

      setChatHistory(prev => [...prev, {
        query: chatQuery,
        answer: data.answer,
        timestamp: data.timestamp
      }]);

      setChatQuery('');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process chat query';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChat();
    }
  };

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Chat with Document
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col h-[700px]">
          <ScrollArea className="flex-1 mb-4">
            <div className="space-y-4">
              {chatHistory.map((chat, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-end">
                    <div className="bg-blue-500 text-white rounded-lg py-2 px-4 max-w-[80%]">
                      {chat.query}
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-gray-100 rounded-lg py-2 px-4 max-w-[80%]">
                      {chat.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Input
              value={chatQuery}
              onChange={(e) => setChatQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask a question..."
              disabled={loading}
            />
            <Button
              onClick={handleChat}
              disabled={loading || !chatQuery.trim()}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartDocChat;